import { debounce } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';
import { useAuth } from './AuthContext';
import { useUser } from './UserContext';

const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const { isAuthenticated, authLoading, user } = useAuth();
  const { 
    progression,          
    enrolledParcours,     
    fetchUserProgression, 
    fetchEnrolledParcours 
  } = useUser();
  
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [cache, setCache] = useState({
    progression: null,
    enrolledParcours: [],
    certificates: [],
    timestamp: 0,
  });

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadDashboardData = useCallback(
    debounce(async (force = false) => {
      if (
        !isAuthenticated ||
        authLoading ||
        isLoading ||
        (dataLoaded && !force && Date.now() - cache.timestamp < CACHE_DURATION)
      ) {
        return;
      }

      console.log('loadDashboardData: exécution');
      setIsLoading(true);

      try {
        const [progressionData, enrolledParcoursData, certificateResponse] = await Promise.all([
          fetchUserProgression(true).catch(() => null),
          fetchEnrolledParcours(true).catch(() => []),
          api.get('/certificates').catch(() => ({ data: [] })),
        ]);

        const enrichedEnrolled = (enrolledParcoursData || []).map(parcours => ({
          ...parcours,
          hasCertificate: certificateResponse.data.some(c => 
            c.parcoursId === parcours.parcoursId?.toString()
          )
        }));

        setCache({
          progression: progressionData,
          enrolledParcours: enrichedEnrolled,
          certificates: certificateResponse.data || [],
          timestamp: Date.now(),
        });

        setDataLoaded(true);
      } catch (error) {
        console.error('loadDashboardData: Erreur:', error);
        toast.error(t('Erreur lors du chargement des données.'));

        setCache(prev => ({
          ...prev,
          certificates: [],
          timestamp: Date.now(),
        }));
        setDataLoaded(true);
      } finally {
        setIsLoading(false);
      }
    }, 1000),
    [isAuthenticated, authLoading, fetchUserProgression, fetchEnrolledParcours, t]
  );

  useEffect(() => {
    if (isAuthenticated && !authLoading && user?._id) {
      loadDashboardData(true);
    }
  }, [isAuthenticated, authLoading, user?._id, loadDashboardData]);

  const recalculateProgression = async () => {
    setIsLoading(true);
    try {
      if (!progression || !user?._id) {
        toast.error(t('Aucune activité libre à recalculer.'));
        return;
      }

      await api.post('/users/me/progression/recalculate', {
        userId: user._id,
        parcoursId: null,
      });

      await fetchUserProgression(true);
      toast.success(t('Progression recalculée avec succès !'));
    } catch (error) {
      console.error('recalculateProgression: Erreur:', error);
      toast.error(t('Erreur lors du recalcul de la progression.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        isLoading,
        dataLoaded,
        cache,
        loadDashboardData,
        recalculateProgression,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard doit être utilisé à l’intérieur de DashboardProvider');
  }
  return context;
};